import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Header from '../../components/ui/Header';
import Sidebar from '../../components/ui/Sidebar';
import Breadcrumb from '../../components/ui/Breadcrumb';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Select from '../../components/ui/Select';
import { coachService, generateCoachLicenseNumber } from '../../services/coachService';
import { refreshCoachLicenseNumber, getCoachGradePrefix } from '../../utils/coachLicenseNumber';
import { teamService } from '../../services/teamService';
import { regionalTeamService } from '../../services/regionalTeamService';
import { TANZANIA_REGION_OPTIONS, getRegionLabel } from '../../utils/tanzaniaRegions';
import {
  calculateLicenseExpirationDate,
  formatValidityMonthsLabel,
  getDefaultLicenseFields,
  parseValidityMonths,
} from '../../utils/coachLicense';
import CoachDocumentsSection from './components/CoachDocumentsSection';
import { getCoachGradeOptions } from '../../constants/coachGrades';
import { DEFAULT_NATIONALITY, nationalityOptions } from '../../data/nationalities';

const GENDER_OPTIONS = [
  { value: 'male', label: 'Male' },
  { value: 'female', label: 'Female' },
  { value: 'other', label: 'Other' },
];

const LICENSE_STATUS_OPTIONS = [
  { value: 'active', label: 'Active' },
  { value: 'expired', label: 'Expired' },
  { value: 'suspended', label: 'Suspended' },
  { value: 'pending', label: 'Pending' },
];

const TEAM_ROLE_OPTIONS = [
  { value: 'Head Coach', label: 'Head Coach' },
  { value: 'Assistant Coach', label: 'Assistant Coach' },
  { value: 'Team Coach', label: 'Team Coach' },
];

const EMPTY_ASSIGNMENT = {
  teamType: 'club',
  teamId: '',
  roleTitle: 'Head Coach',
  isPrimary: false,
};

const CoachRegistration = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const editCoachId = searchParams.get('edit');
  const isEditMode = !!editCoachId;

  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoading, setIsLoading] = useState(isEditMode);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [clubTeams, setClubTeams] = useState([]);
  const [regionalTeams, setRegionalTeams] = useState([]);
  const [teamAssignments, setTeamAssignments] = useState([]);
  const [assignmentDraft, setAssignmentDraft] = useState(EMPTY_ASSIGNMENT);

  const [existingCertificates, setExistingCertificates] = useState([]);
  const [newCertificateFiles, setNewCertificateFiles] = useState([]);
  const [removedCertificateIds, setRemovedCertificateIds] = useState([]);
  const [existingPassport, setExistingPassport] = useState(null);

  const [formData, setFormData] = useState({
    coachName: '',
    grade: '',
    region: '',
    nationality: DEFAULT_NATIONALITY,
    gender: '',
    mobile: '',
    email: '',
    physicalAddress: '',
    dateOfBirth: '',
    licenseNumber: '',
    licenseStatus: 'pending',
    ...getDefaultLicenseFields(),
    photoFile: null,
    photoFileName: '',
    passportFile: null,
    passportFileName: '',
  });
  const [errors, setErrors] = useState({});

  const gradeOptions = useMemo(
    () => getCoachGradeOptions(formData.grade),
    [formData.grade]
  );

  useEffect(() => {
    const loadTeams = async () => {
      try {
        const [clubs, regional] = await Promise.all([
          teamService.getAll(),
          regionalTeamService.getAll(),
        ]);
        setClubTeams(clubs || []);
        setRegionalTeams(regional || []);
      } catch (err) {
        console.warn('Failed to load teams for coach assignment:', err);
      }
    };
    loadTeams();
  }, []);

  useEffect(() => {
    if (!isEditMode || !editCoachId) return;

    const loadCoach = async () => {
      setIsLoading(true);
      try {
        const coach = await coachService.getById(editCoachId);
        setFormData({
          coachName: coach.coachName || '',
          grade: coach.grade || '',
          region: coach.region || '',
          nationality: coach.nationality || DEFAULT_NATIONALITY,
          gender: coach.gender || '',
          mobile: coach.mobile || '',
          email: coach.email || '',
          physicalAddress: coach.physicalAddress || '',
          dateOfBirth: coach.dateOfBirth || '',
          licenseNumber: refreshCoachLicenseNumber(coach.licenseNumber || '', coach.grade),
          licenseStatus: coach.licenseStatus || 'pending',
          validityPeriod: String(parseValidityMonths(coach.validityPeriod)),
          licenseExpirationDate: coach.licenseExpirationDate || calculateLicenseExpirationDate(coach.validityPeriod),
          photoFile: null,
          photoFileName: coach.coachPhoto?.fileName || '',
          passportFile: null,
          passportFileName: coach.passport?.fileName || '',
        });
        setExistingCertificates(coach.coachingCertificates || []);
        setExistingPassport(coach.passport || null);
        setNewCertificateFiles([]);
        setRemovedCertificateIds([]);
        setTeamAssignments(
          (coach.teamAssignments || []).map((assignment) => ({
            teamType: assignment.regionalTeamId ? 'regional' : 'club',
            teamId: assignment.regionalTeamId || assignment.teamId,
            roleTitle: assignment.roleTitle || 'Head Coach',
            isPrimary: assignment.isPrimary,
          }))
        );
      } catch (err) {
        setError(err.message || 'Failed to load coach');
      } finally {
        setIsLoading(false);
      }
    };

    loadCoach();
  }, [isEditMode, editCoachId]);

  const teamOptions = assignmentDraft.teamType === 'regional'
    ? regionalTeams.map((team) => ({
        value: team.id,
        label: `${team.teamName} (${getRegionLabel(team.region)})`,
      }))
    : clubTeams.map((team) => ({
        value: team.id,
        label: team.name || team.teamName,
      }));

  const handleInputChange = (field, value) => {
    setFormData((prev) => {
      const next = { ...prev, [field]: value };
      if (field === 'validityPeriod') {
        next.licenseExpirationDate = calculateLicenseExpirationDate(value);
      }
      if (field === 'grade') {
        next.licenseNumber = refreshCoachLicenseNumber(prev.licenseNumber, value);
      }
      return next;
    });
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: '' }));
    }
    if (error) setError(null);
    if (success) setSuccess(null);
  };

  const handlePhotoChange = (file) => {
    if (!file) {
      setFormData((prev) => ({ ...prev, photoFile: null, photoFileName: '' }));
      return;
    }
    setFormData((prev) => ({
      ...prev,
      photoFile: file,
      photoFileName: file.name,
    }));
  };

  const handlePassportChange = (file) => {
    setFormData((prev) => ({
      ...prev,
      passportFile: file,
      passportFileName: file?.name || '',
    }));
  };

  const handleAddCertificates = (files) => {
    if (!files?.length) return;
    setNewCertificateFiles((prev) => [...prev, ...files]);
    if (errors.certificateFile) {
      setErrors((prev) => ({ ...prev, certificateFile: '' }));
    }
  };

  const handleRemoveExistingCertificate = (documentId) => {
    setExistingCertificates((prev) => prev.filter((item) => item.id !== documentId));
    setRemovedCertificateIds((prev) => [...prev, documentId]);
  };

  const handleRemoveNewCertificate = (index) => {
    setNewCertificateFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const getTotalCertificateCount = () =>
    existingCertificates.length + newCertificateFiles.length;

  const handleAddTeamAssignment = () => {
    if (!assignmentDraft.teamId) {
      setErrors((prev) => ({ ...prev, teamAssignment: 'Please select a team' }));
      return;
    }
    const duplicate = teamAssignments.some(
      (item) => item.teamType === assignmentDraft.teamType && item.teamId === assignmentDraft.teamId
    );
    if (duplicate) {
      setErrors((prev) => ({ ...prev, teamAssignment: 'This team is already assigned' }));
      return;
    }
    setTeamAssignments((prev) => [...prev, { ...assignmentDraft }]);
    setAssignmentDraft(EMPTY_ASSIGNMENT);
    setErrors((prev) => ({ ...prev, teamAssignment: '' }));
  };

  const handleRemoveAssignment = (index) => {
    setTeamAssignments((prev) => prev.filter((_, i) => i !== index));
  };

  const validateForm = () => {
    const nextErrors = {};
    if (!formData.coachName?.trim()) nextErrors.coachName = 'Coach name is required';
    if (!formData.grade) nextErrors.grade = 'Grade is required';
    if (!formData.region) nextErrors.region = 'Region is required';
    if (!formData.nationality) nextErrors.nationality = 'Nationality is required';
    if (!formData.gender) nextErrors.gender = 'Gender is required';
    if (!formData.mobile?.trim()) nextErrors.mobile = 'Mobile number is required';
    if (!formData.email?.trim()) {
      nextErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      nextErrors.email = 'Please enter a valid email address';
    }
    if (!formData.physicalAddress?.trim()) nextErrors.physicalAddress = 'Physical address is required';
    if (!formData.dateOfBirth) nextErrors.dateOfBirth = 'Birth date is required';
    if (!formData.licenseNumber?.trim() && !formData.grade) {
      nextErrors.licenseNumber = 'Select a grade to generate a license number';
    }
    if (!formData.licenseStatus) nextErrors.licenseStatus = 'License status is required';
    const validityMonths = parseValidityMonths(formData.validityPeriod);
    if (!validityMonths || validityMonths < 1) {
      nextErrors.validityPeriod = 'Validity period must be at least 1 month';
    }
    if (!formData.licenseExpirationDate) {
      nextErrors.licenseExpirationDate = 'License expiration date could not be calculated';
    }
    if (getTotalCertificateCount() < 1) {
      nextErrors.certificateFile = 'At least one coaching certificate is required';
    }
    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setIsSubmitting(true);
    setError(null);
    setSuccess(null);

    const payload = {
      coachName: formData.coachName.trim(),
      grade: formData.grade,
      region: formData.region,
      nationality: formData.nationality,
      gender: formData.gender,
      mobile: formData.mobile.trim(),
      email: formData.email.trim(),
      physicalAddress: formData.physicalAddress.trim(),
      dateOfBirth: formData.dateOfBirth,
      licenseNumber: formData.licenseNumber.trim() || generateCoachLicenseNumber(formData.grade),
      licenseStatus: formData.licenseStatus,
      validityPeriod: String(parseValidityMonths(formData.validityPeriod)),
      licenseExpirationDate: calculateLicenseExpirationDate(formData.validityPeriod),
      photoFile: formData.photoFile,
      passportFile: formData.passportFile,
      certificateFiles: newCertificateFiles,
      removedCertificateIds,
      teamAssignments,
    };

    try {
      if (isEditMode) {
        await coachService.update(editCoachId, payload);
        setSuccess('Coach updated successfully.');
        setTimeout(() => navigate('/league-coaches'), 1500);
      } else {
        await coachService.registerCoach(payload);
        setSuccess('Coach registered successfully. The coaching license is pending admin approval.');
        setTimeout(() => navigate('/league-coaches'), 1500);
      }
    } catch (err) {
      setError(err.message || 'Failed to save coach');
    } finally {
      setIsSubmitting(false);
    }
  };

  const breadcrumbItems = [
    { label: 'Home', path: '/admin-dashboard' },
    { label: 'League Coaches', path: '/league-coaches' },
    { label: isEditMode ? 'Edit Coach' : 'Coach Registration' },
  ];

  const getAssignmentLabel = (assignment) => {
    if (assignment.teamType === 'regional') {
      const team = regionalTeams.find((item) => item.id === assignment.teamId);
      return team ? `${team.teamName} (${getRegionLabel(team.region)})` : 'Regional Team';
    }
    const team = clubTeams.find((item) => item.id === assignment.teamId);
    return team?.name || 'Club Team';
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <Sidebar
        isCollapsed={sidebarCollapsed}
        onToggle={() => setSidebarCollapsed(!sidebarCollapsed)}
      />
      <main className={`pt-16 transition-all duration-300 ${sidebarCollapsed ? 'lg:ml-16' : 'lg:ml-72'}`}>
        <div className="p-6">
          <Breadcrumb items={breadcrumbItems} />

          <div className="mt-4 mb-6">
            <h1 className="text-2xl font-bold text-foreground">
              {isEditMode ? 'Edit Coach Information' : 'New Coach Information'}
            </h1>
            <p className="text-muted-foreground mt-1">
              Register basketball coaches with license, personal details, and team assignments.
            </p>
          </div>

          {success && (
            <div className="mb-6 px-4 py-3 rounded-lg border bg-success/10 border-success/20 text-success">
              <div className="flex items-start gap-3">
                <Icon name="CheckCircle2" size={18} />
                <p className="font-medium">{success}</p>
              </div>
            </div>
          )}

          {error && (
            <div className="mb-6 px-4 py-3 rounded-lg border bg-destructive/10 border-destructive/20 text-destructive">
              <div className="flex items-start gap-3">
                <Icon name="AlertTriangle" size={18} />
                <p className="font-medium">{error}</p>
              </div>
            </div>
          )}

          {isLoading ? (
            <div className="flex justify-center py-12">
              <Icon name="Loader2" size={32} className="text-primary animate-spin" />
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-6">
              <section className="bg-card border border-border rounded-lg card-shadow p-6 space-y-4">
                <h2 className="text-lg font-semibold text-foreground border-b border-border pb-3">
                  Coaching Information
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Input
                    label="Coach Name"
                    value={formData.coachName}
                    onChange={(e) => handleInputChange('coachName', e.target.value)}
                    error={errors.coachName}
                    required
                  />
                  <Select
                    label="Grade"
                    placeholder="Please Select"
                    options={gradeOptions}
                    value={formData.grade}
                    onChange={(value) => handleInputChange('grade', value)}
                    error={errors.grade}
                    required
                  />
                  <Select
                    label="Region"
                    placeholder="Please Select"
                    options={TANZANIA_REGION_OPTIONS}
                    value={formData.region}
                    onChange={(value) => handleInputChange('region', value)}
                    error={errors.region}
                    required
                  />
                  <Select
                    label="Gender"
                    placeholder="Please Select"
                    options={GENDER_OPTIONS}
                    value={formData.gender}
                    onChange={(value) => handleInputChange('gender', value)}
                    error={errors.gender}
                    required
                  />
                  <Select
                    label="Nationality"
                    placeholder="Please Select"
                    options={nationalityOptions}
                    value={formData.nationality}
                    onChange={(value) => handleInputChange('nationality', value)}
                    error={errors.nationality}
                    required
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-4 gap-4 items-end pt-2">
                  <Select
                    label="Team Type"
                    options={[
                      { value: 'club', label: 'Club Team' },
                      { value: 'regional', label: 'Regional Team' },
                    ]}
                    value={assignmentDraft.teamType}
                    onChange={(value) => setAssignmentDraft((prev) => ({ ...prev, teamType: value, teamId: '' }))}
                  />
                  <Select
                    label="Team"
                    placeholder="Please Select"
                    options={teamOptions}
                    value={assignmentDraft.teamId}
                    onChange={(value) => setAssignmentDraft((prev) => ({ ...prev, teamId: value }))}
                  />
                  <Select
                    label="Role"
                    options={TEAM_ROLE_OPTIONS}
                    value={assignmentDraft.roleTitle}
                    onChange={(value) => setAssignmentDraft((prev) => ({ ...prev, roleTitle: value }))}
                  />
                  <Button
                    type="button"
                    variant="outline"
                    iconName="UserPlus"
                    onClick={handleAddTeamAssignment}
                  >
                    Add Team Coach
                  </Button>
                </div>
                {errors.teamAssignment && (
                  <p className="text-sm text-destructive">{errors.teamAssignment}</p>
                )}

                {teamAssignments.length > 0 && (
                  <div className="space-y-2">
                    {teamAssignments.map((assignment, index) => (
                      <div
                        key={`${assignment.teamType}-${assignment.teamId}-${index}`}
                        className="flex items-center justify-between p-3 bg-muted/30 border border-border rounded-md"
                      >
                        <div>
                          <p className="text-sm font-medium text-foreground">{getAssignmentLabel(assignment)}</p>
                          <p className="text-xs text-muted-foreground">{assignment.roleTitle}</p>
                        </div>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => handleRemoveAssignment(index)}
                        >
                          <Icon name="X" size={16} />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}

                <CoachDocumentsSection
                  photoFileName={formData.photoFileName}
                  onPhotoChange={handlePhotoChange}
                  existingCertificates={existingCertificates}
                  newCertificateFiles={newCertificateFiles}
                  onAddCertificates={handleAddCertificates}
                  onRemoveExistingCertificate={handleRemoveExistingCertificate}
                  onRemoveNewCertificate={handleRemoveNewCertificate}
                  existingPassport={existingPassport}
                  passportFileName={formData.passportFileName}
                  onPassportChange={handlePassportChange}
                  errors={errors}
                />
              </section>

              <section className="bg-card border border-border rounded-lg card-shadow p-6 space-y-4">
                <h2 className="text-lg font-semibold text-foreground border-b border-border pb-3">
                  Personal Information
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Input
                    label="Mobile"
                    type="tel"
                    value={formData.mobile}
                    onChange={(e) => handleInputChange('mobile', e.target.value)}
                    error={errors.mobile}
                    required
                  />
                  <Input
                    label="Email"
                    type="email"
                    value={formData.email}
                    onChange={(e) => handleInputChange('email', e.target.value)}
                    error={errors.email}
                    required
                  />
                  <Input
                    label="Physical Address"
                    value={formData.physicalAddress}
                    onChange={(e) => handleInputChange('physicalAddress', e.target.value)}
                    error={errors.physicalAddress}
                    required
                  />
                  <Input
                    label="Birth Date"
                    type="date"
                    value={formData.dateOfBirth}
                    onChange={(e) => handleInputChange('dateOfBirth', e.target.value)}
                    error={errors.dateOfBirth}
                    required
                  />
                </div>
              </section>

              <section className="bg-card border border-border rounded-lg card-shadow p-6 space-y-4">
                <h2 className="text-lg font-semibold text-foreground border-b border-border pb-3">
                  License Information
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Input
                    label="License Number"
                    value={formData.licenseNumber}
                    onChange={(e) => handleInputChange('licenseNumber', e.target.value)}
                    error={errors.licenseNumber}
                    description={
                      formData.grade
                        ? `Auto-generated for ${formData.grade} (e.g. TBF-${getCoachGradePrefix(formData.grade)}-${new Date().getFullYear()}-XXXX)`
                        : 'Select a grade to generate the license number'
                    }
                    required
                  />
                  <Select
                    label="License Status"
                    placeholder="Please Select"
                    options={LICENSE_STATUS_OPTIONS}
                    value={formData.licenseStatus}
                    onChange={(value) => handleInputChange('licenseStatus', value)}
                    error={errors.licenseStatus}
                    required
                  />
                  <Input
                    label="Validity Period (months)"
                    type="number"
                    min={1}
                    max={120}
                    value={formData.validityPeriod}
                    onChange={(e) => handleInputChange('validityPeriod', e.target.value)}
                    error={errors.validityPeriod}
                    required
                  />
                  <Input
                    label="License Expiration Date"
                    type="date"
                    value={formData.licenseExpirationDate}
                    readOnly
                    disabled
                    description={`Auto-calculated from ${formatValidityMonthsLabel(formData.validityPeriod)} from today`}
                    error={errors.licenseExpirationDate}
                    required
                  />
                </div>
              </section>

              <div className="flex items-center justify-end gap-3">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => navigate('/league-coaches')}
                  disabled={isSubmitting}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  loading={isSubmitting}
                  iconName={isEditMode ? 'Save' : 'UserPlus'}
                >
                  {isEditMode ? 'Update Coach' : 'Register Coach'}
                </Button>
              </div>
            </form>
          )}
        </div>
      </main>
    </div>
  );
};

export default CoachRegistration;
